import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Rarity } from '@anidex/shared/economy';
import type { UserStatsDTO } from '@anidex/shared/types';
import { formatNumber, rarityLabel } from '../services/format';
import { useVisibleRarities } from '../services/queries';

interface RarityStripProps {
  stats: Pick<UserStatsDTO, 'ownedByRarity' | 'catalogByRarity'>;
  
  onSelect?: (rarity: Rarity | '') => void;
  active?: Rarity | '';
}

function Entry({ label, owned, total }: { label: string; owned: number; total: number }) {
  const percent = total > 0 ? Math.min(100, (owned / total) * 100) : 0;
  return (
    <>
      <span className="rarity-strip__gem" aria-hidden="true" />
      <span className="rarity-strip__label">{label}</span>
      <span className="rarity-strip__count">
        {formatNumber(owned)}
        <span> / {formatNumber(total)}</span>
      </span>
      <span className="rarity-strip__bar" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </span>
    </>
  );
}


export function RarityStrip({ stats, onSelect, active = '' }: RarityStripProps) {
  const item = (key: Rarity | '', content: ReactNode, className: string) =>
    onSelect ? (
      <button type="button" className={`rarity-strip__item ${className}`} aria-pressed={active === key} onClick={() => onSelect(active === key && key !== '' ? '' : key)}>
        {content}
      </button>
    ) : (
      <div className={`rarity-strip__item ${className}`}>{content}</div>
    );

  const { t } = useTranslation();
  const rarities = useVisibleRarities();
  const owned = rarities.reduce((sum, rarity) => sum + stats.ownedByRarity[rarity], 0);
  const total = rarities.reduce((sum, rarity) => sum + stats.catalogByRarity[rarity], 0);

  return (
    <ul className="rarity-strip" aria-label={onSelect ? t('rarity.filter') : t('rarity.progress')}>
      {onSelect && <li>{item('', <Entry label={t('rarity.all')} owned={owned} total={total} />, 'rarity-strip__item--all')}</li>}
      {[...rarities].reverse().map((rarity) => (
        <li key={rarity} className={`rarity-${rarity}`}>
          {item(rarity, <Entry label={rarityLabel(rarity)} owned={stats.ownedByRarity[rarity]} total={stats.catalogByRarity[rarity]} />, '')}
        </li>
      ))}
    </ul>
  );
}
